const PUBLIC_SITE_URL =
  import.meta.env.VITE_SITE_URL ??
  (typeof window !== 'undefined' && window.location.origin ? window.location.origin : 'https://advoltix.com');

const SITE_URL = (PUBLIC_SITE_URL ?? 'https://advoltix.com').replace(/\/$/, '');
const LOGO_URL = `${SITE_URL}/advoltix-favicon.png`;

export type RouteSeoEntry = {
  pattern: string;
  exact?: boolean;
  title?: string;
  description?: string;
  image?: string;
  keywords?: string[];
  noindex?: boolean;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
  ogType?: 'website' | 'article' | 'product';
};

const DEFAULT_DESCRIPTION =
  'Advoltix is the all-in-one platform for performance marketing agencies: Meta, Google & TikTok ad management, AI ad automation, a built-in CRM, email campaigns, and white-label client reporting from one dashboard.';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Advoltix',
  url: SITE_URL,
  logo: LOGO_URL,
  sameAs: ['https://www.linkedin.com/company/advoltix'],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      email: 'hello@advoltix.com',
      contactType: 'customer service',
      availableLanguage: ['English'],
    },
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Advoltix',
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const supportFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What channels does Advoltix support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Advoltix supports paid social, search, programmatic, marketplace, and conversational commerce workflows.',
      },
    },
    {
      '@type': 'Question',
      name: 'How fast is onboarding?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most teams connect their data sources and launch guardrails within the first week using our guided onboarding.',
      },
    },
  ],
};

export const DEFAULT_SEO_ENTRY: RouteSeoEntry = {
  pattern: '*',
  exact: false,
  title: 'Advoltix — AI Ad Management, CRM & White-Label Reporting for Agencies',
  description: DEFAULT_DESCRIPTION,
  image: LOGO_URL,
  keywords: ['ad management platform', 'marketing agency software', 'Meta Google TikTok ads', 'AI ad automation', 'white-label reporting', 'marketing CRM', 'email campaigns'],
};

export const ROUTE_SEO_CONFIG: RouteSeoEntry[] = [
  {
    pattern: '/',
    exact: true,
    title: 'Advoltix — AI Ad Management, CRM & White-Label Reporting for Agencies',
    description:
      'The all-in-one platform for performance marketing agencies. Manage Meta, Google & TikTok ads, AI ad automation, a built-in CRM, email campaigns, and white-label client reporting from one dashboard. 14-day free trial.',
    keywords: [
      'ad management platform', 'marketing agency software', 'Meta ads automation',
      'Google Ads automation', 'TikTok ads platform', 'AI ad optimization',
      'white-label reporting', 'marketing CRM', 'email campaign software',
      'performance marketing platform', 'agency client reporting', 'ads automation',
    ],
    structuredData: [organizationSchema, websiteSchema],
  },
  {
    pattern: '/platform',
    exact: true,
    title: 'Platform Overview',
    description: 'Command center for omnichannel growth with cross-team guardrails, reporting, and localization.',
    keywords: ['omnichannel platform', 'marketing command center', 'automation'],
  },
  {
    pattern: '/solutions/automations',
    exact: true,
    title: 'Automation Guardrails & AI Playbooks',
    description: 'Define guardrails for spend, CX, and fulfillment outcomes with explainable AI interventions.',
    keywords: ['automation', 'guardrails', 'ai marketing'],
  },
  {
    pattern: '/solutions/integrations',
    exact: true,
    title: 'Integrations & Data Connectivity',
    description: 'Connect ad networks, commerce systems, CRMs, and warehouses with secure governance.',
    keywords: ['marketing integrations', 'data connectivity', 'api'],
  },
  {
    pattern: '/company/about',
    exact: true,
    title: 'About Advoltix',
    description: 'Learn about the team enabling agencies and brands to deliver predictable growth.',
    keywords: ['about advoltix', 'growth team', 'company'],
  },
  {
    pattern: '/company/security',
    exact: true,
    title: 'Security & Trust Center',
    description: 'Discover Advoltix security controls, privacy practices, and compliance readiness.',
    keywords: ['security', 'trust center', 'compliance'],
  },
  {
    pattern: '/company/careers',
    exact: true,
    title: 'Careers',
    description: 'Join Advoltix and help ship AI-first tooling for modern revenue teams.',
    keywords: ['careers', 'jobs', 'advoltix'],
    ogType: 'article',
  },
  {
    pattern: '/resources/case-studies',
    exact: true,
    title: 'Case Studies',
    description: 'See how agencies and brands improve ROI, retention, and guardrail coverage with Advoltix.',
    keywords: ['case studies', 'customer stories', 'roi'],
    ogType: 'article',
  },
  {
    pattern: '/resources/blog',
    exact: true,
    title: 'Insights & Blog',
    description: 'Operational playbooks and automation best practices for modern marketers.',
    keywords: ['marketing blog', 'automation insights'],
    ogType: 'article',
  },
  {
    pattern: '/resources/support',
    exact: true,
    title: 'Global Support',
    description: '24/7 multilingual assistance for onboarding, compliance, and automation reviews.',
    keywords: ['support', 'help center', 'customer success'],
    structuredData: supportFaqSchema,
  },
  {
    pattern: '/legal/privacy',
    exact: true,
    title: 'Privacy Notice',
    description: 'Understand how Advoltix collects, processes, and protects personal data.',
    keywords: ['privacy policy', 'data protection'],
    ogType: 'article',
  },
  {
    pattern: '/legal/terms',
    exact: true,
    title: 'Terms of Service',
    description: 'Review the master subscription agreement governing Advoltix services.',
    keywords: ['terms of service', 'msa'],
    ogType: 'article',
  },
  {
    pattern: '/legal/compliance',
    exact: true,
    title: 'Compliance & Governance',
    description: 'Map global frameworks like GDPR, SOC2, and ISO 27001 to Advoltix controls.',
    keywords: ['compliance', 'governance', 'gdpr'],
  },
  {
    pattern: '/auth/login',
    exact: true,
    title: 'Login',
    description: 'Securely sign in to orchestrate campaigns, automation, and compliance.',
    keywords: ['login', 'advoltix account'],
    noindex: true,
  },
  {
    pattern: '/auth/register',
    exact: true,
    title: 'Create an Account',
    description: 'Start your Advoltix agency account to manage every client, campaign, and report in one place.',
    keywords: ['register', 'signup'],
    noindex: true,
  },
  {
    pattern: '/auth/forgot-password',
    exact: true,
    title: 'Password Recovery',
    description: 'Reset your Advoltix credentials.',
    keywords: ['password reset'],
    noindex: true,
  },
  {
    pattern: '/auth/callback',
    exact: true,
    title: 'Finishing Authentication',
    description: 'Complete secure social login with Advoltix.',
    noindex: true,
  },
  {
    pattern: '/auth/error',
    exact: true,
    title: 'Authentication Error',
    description: 'Resolve issues connecting your Advoltix account.',
    noindex: true,
  },
  {
    pattern: '/app/dashboard',
    title: 'Agency Dashboard',
    description: 'Monitor every client, campaign, and spend across your agency.',
    keywords: ['dashboard', 'marketing analytics'],
    noindex: true,
  },
  {
    pattern: '/app/analytics',
    title: 'Analytics',
    description: 'Drill into paid media and automation analytics with live data.',
    noindex: true,
  },
  {
    pattern: '/app/campaigns',
    title: 'Campaign Pipeline',
    description: 'Track campaign approvals, budget pacing, and creative health.',
    noindex: true,
  },
  {
    pattern: '/app/journeys',
    title: 'Journey Builder',
    description: 'Design multi-touch customer journeys with experimentation controls.',
    noindex: true,
  },
  {
    pattern: '/app/automation',
    title: 'Automation Timeline',
    description: 'Review AI guardrail actions, compliance logs, and approvals.',
    noindex: true,
  },
  {
    pattern: '/app/personalization',
    title: 'Personalization Studio',
    description: 'Evaluate experiences, sentiment, and VOC insights.',
    noindex: true,
  },
  {
    pattern: '/app/moderation',
    title: 'Content Moderation',
    description: 'Verify creatives and scripts across markets.',
    noindex: true,
  },
  {
    pattern: '/app/api-keys',
    title: 'API Keys',
    description: 'Manage API tokens and integrations.',
    noindex: true,
  },
  {
    pattern: '/app/team',
    title: 'Team Management',
    description: 'Invite teammates, assign roles, and enable SSO.',
    noindex: true,
  },
  {
    pattern: '/app/organization',
    title: 'Organization Settings',
    description: 'Configure brand defaults, domains, and governance.',
    noindex: true,
  },
  {
    pattern: '/app/billing',
    title: 'Billing',
    description: 'Manage subscriptions, invoices, and usage.',
    noindex: true,
  },
  {
    pattern: '/app/integrations',
    title: 'Integrations',
    description: 'Connect ad accounts, marketplaces, and data warehouses.',
    noindex: true,
  },
  {
    pattern: '/app/settings',
    title: 'Settings',
    description: 'Configure workspace preferences and compliance controls.',
    noindex: true,
  },
  {
    pattern: '/app/*',
    exact: false,
    title: 'Advoltix',
    description: 'Manage every client, campaign, and report from one agency command center.',
    noindex: true,
  },
];

